import { useFormik } from "formik";
import CachedIcon from "mdi-react/CachedIcon";
import React, { useState } from "react";
import { Modal, Form, Button, Table, InputGroup } from "react-bootstrap";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { useBackendUrl } from "../../utils/hooks";
import ConfirmDialog from "../ConfirmDialogue";
import { DeleteIcon } from "../Icons";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import ExpensesSelector from "../utils/ExpensesSelector";
import { Popover } from "react-tiny-popover";

export default function ManageLoanModal({
  showMangeLoanModal,
  selectedBank,
  setShowMangeLoanModal,
}) {
  const backendUrl = useBackendUrl();
  const [showJournalPopover, setShowJournalPopover] = useState(false);

  const fetchLoanAccounts = async () => {
    let response = await fetch(
      `${backendUrl}/api/banks/loan-accounts?BankName=${encodeURIComponent(
        selectedBank.BankName
      )}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const { data } = await response.json();
    return data;
  };

  const { data = { loanAccounts: [] }, refetch } = useQuery(
    ["BANK_LOAN_ACCOUNTS", selectedBank?.BankName],
    () => fetchLoanAccounts(),
    { keepPreviousData: true, enabled: !!selectedBank?.BankName }
  );

  // ── account selector ───────────────────────────────────────────
  const handleSelectedAccount = account => {
    formik.setFieldValue("AccountID", account?.AccountID);
    formik.setFieldValue("AccountDescription", account?.Description);
    formik.setFieldValue("AccountType", account?.Type);
    setShowJournalPopover(false);
  };

  // ── POST mutation ──────────────────────────────────────────────
  const addLoanAccount = async payload => {
    let response = await fetch(`${backendUrl}/api/banks/loan-accounts`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify(payload),
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
    });
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    return response.json();
  };

  const addLoanAccountMutation = useMutation(
    payload => addLoanAccount(payload),
    {
      onSuccess: ({ message }) => {
        toast.success(message);
        formik.resetForm();
        refetch();
      },
      onError: ({ message = "" }) =>
        toast.error(`Unable to perform action: ${message}`),
    }
  );

  // ── DELETE mutation ────────────────────────────────────────────
  const deleteLoanAccount = async ({ BankName, AccountID }) => {
    let response = await fetch(
      `${backendUrl}/api/banks/loan-accounts/${encodeURIComponent(
        BankName
      )}/${encodeURIComponent(AccountID)}`,
      { method: "DELETE", credentials: "include" }
    );
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    return response.json();
  };

  const deleteLoanAccountMutation = useMutation(
    payload => deleteLoanAccount(payload),
    {
      onSuccess: ({ message }) => {
        toast.success(message);
        refetch();
      },
      onError: ({ message = "" }) =>
        toast.error(`Unable to perform action: ${message}`),
    }
  );

  const formik = useFormik({
    initialValues: { AccountID: "", AccountDescription: "", AccountType: "" },
    validate: values => {
      const errors = {};
      if (!values.AccountID) errors.AccountID = "Select an account";
      return errors;
    },
    onSubmit: async values => {
      if (
        await ConfirmDialog({
          title: "Add Loan Account",
          description: "Link this account to the bank?",
        })
      ) {
        addLoanAccountMutation.mutate({
          BankName: selectedBank.BankName,
          ...values,
        });
      }
    },
  });

  const handleDelete = async el => {
    if (
      await ConfirmDialog({
        title: "Remove Loan Account",
        description: `Remove ${el.AccountDescription}?`,
        type: "danger",
      })
    ) {
      deleteLoanAccountMutation.mutate({
        BankName: el.BankName,
        AccountID: el.AccountID,
      });
    }
  };

  return (
    <Modal
      show={showMangeLoanModal}
      onHide={() => setShowMangeLoanModal(false)}
      size="md"
      centered
      animation={false}
      autoFocus={false}
      enforceFocus={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="h5">
          {selectedBank.BankName}
          <Button variant="" className="text-primary" onClick={() => refetch()}>
            <CachedIcon />
          </Button>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
          <Form.Group className="mb-3">
            <Form.Label>Select Account</Form.Label>
            <InputGroup hasValidation>
              <Form.Control
                placeholder="Select Account"
                value={
                  formik.values.AccountID
                    ? `${formik.values.AccountID} / ${
                        formik.values.AccountDescription
                      } / ${formik.values.AccountType}`
                    : ""
                }
                onChange={() => {}}
                readOnly
                isInvalid={
                  formik.touched.AccountID && !!formik.errors.AccountID
                }
              />
              <Popover
                isOpen={showJournalPopover}
                onClickOutside={() => setShowJournalPopover(false)}
                content={() => (
                  <ExpensesSelector
                    handleSelectedExpense={handleSelectedAccount}
                    usage="chart of accounts"
                    onHide={() => setShowJournalPopover(false)}
                  />
                )}
                position="bottom"
              >
                <InputGroup.Text onClick={() => setShowJournalPopover(true)}>
                  <MagnifyIcon />
                </InputGroup.Text>
              </Popover>
              <Form.Control.Feedback type="invalid">
                {formik.errors.AccountID}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>

          <div className="d-flex gap-3 justify-content-end">
            <Button type="submit" disabled={addLoanAccountMutation.isLoading}>
              {addLoanAccountMutation.isLoading
                ? "Please wait…"
                : "Add Account"}
            </Button>
          </div>
        </Form>

        <Table borderless striped className="product-table mt-4 border">
          <thead>
            <tr>
              <th />
              <th>Account ID</th>
              <th>Description</th>
              <th>Type</th>
            </tr>
          </thead>
          <tbody>
            {data.loanAccounts.map((el, index) => (
              <tr key={index}>
                <td style={{ whiteSpace: "nowrap", width: "1%" }}>
                  <Button variant="" onClick={() => handleDelete(el)}>
                    <DeleteIcon />
                  </Button>
                </td>
                <td>{el.AccountID}</td>
                <td>{el.AccountDescription}</td>
                <td>{el.AccountType}</td>
              </tr>
            ))}
            {data.loanAccounts.length === 0 && (
              <tr>
                <td colSpan="4" className="text-center text-muted py-3">
                  No loan accounts linked yet
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Modal.Body>
    </Modal>
  );
}
